import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Slide, ToastContainer, toast } from "react-toastify";
import { Title } from "../../../../../../themes/Default/components/Title";
import { useTranslation } from "react-i18next";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { List } from "../../../../../../themes/Default/components/List";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Checkbox } from "../../../../../../themes/Default/components/Form/components/Checkbox";
import { Pagination } from "../../../../../../themes/Default/components/List/components/Footer/components/Pagination";
import { Toaster } from "../../../../../../themes/Default/components/Toaster";
import { settingsCallManagement } from "../../../../../../hooks/api/SettingsCallManagement";

const MethodesOfContact = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 50;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const navigate = useNavigate();
    const [filters, setFilters] = useState(defaultFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedMethodesOfContact, setSelectedMethodesOfContact] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [methodesOfContact, setMethodesOfContact] = useState();
    const { useMethodOfContactListMutation, useDeleteMethodOfContactMutation } = settingsCallManagement;
    const [methodOfContactList] = useMethodOfContactListMutation();
    const [deleteMethodOfContact] = useDeleteMethodOfContactMutation();

    const tabs = [
        { title: t('calls.appointment'), onClick: () => gotoAppointmentTab() },
        { title: t("calls.courseType"), onClick: () => gotoCourseTypeTab() },
        { title: t("calls.methodOfContact"), isActive: true },
        { title: t("calls.salesChannel"), onClick: () => gotoSalesChannelTab() },
        { title: t("calls.studentStatus"), onClick: () => gotoStudentStatusTab() },
        { title: t("calls.status"), onClick: () => gotoStatusTab() },
        { title: t("calls.offerSent"), onClick: () => gotoOfferSentTab() },
    ];

    const gotoOfferSentTab = () => {
        navigate("/settings/calls/offer-sent");
    };

    const gotoAppointmentTab = () => {
        navigate("/settings/calls/appointments");
    };

    const gotoCourseTypeTab = () => {
        navigate("/settings/calls/course-type");
    };

    const gotoSalesChannelTab = () => {
        navigate("/settings/calls/sales-channel");
    };

    const gotoStudentStatusTab = () => {
        navigate("/settings/calls/student-status");
    };

    const gotoStatusTab = () => {
        navigate("/settings/calls/status");
    };

    const gotoNew = () => {
        navigate("/settings/calls/method-of-contact/add");
    };

    const gotoEdit = (methodOfContactId) => {
        navigate("/settings/calls/method-of-contact/edit/" + methodOfContactId);
    };

    const onPageChange = (currentPage, startPage, limitPage) => {
        setCurrentPage(currentPage);
        setFilters({
            ...filters,
            startPage: startPage,
            limitPage: limitPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedMethodesOfContact((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedMethodesOfContact?.length; i++) {
                            const methodOfContactId = selectedMethodesOfContact[i];
                            deleteMethodOfContact(methodOfContactId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    methodOfContactList(filters).then((result) => {
                                        const methodOfContacts = result?.data?.records;
                                        setMethodesOfContact(methodOfContacts);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                    onSelectionChange(methodOfContactId);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                closeButton: false,
                autoClose: false,
                progress: false,
            },
        );
    };

    useEffect(() => {
        if (selectedMethodesOfContact.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedMethodesOfContact]);

    useEffect(() => {
        methodOfContactList(filters).then((result) => {
            const methodOfContacts = result?.data?.records;
            setMethodesOfContact(methodOfContacts);
            setTotalRecords(result?.data?.totalRecords);
        });
    }, [filters, methodOfContactList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings" />
                <Button title={t("calls.addMethodOfContact")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("calls.methodOfContact")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("calls.title")} />
                    <Link title={t("calls.methodOfContact")} isActive={true} />
                </Breadcrumps>
                        <List>
                            <ListHeader>
                            </ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("languages.name")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {methodesOfContact?.length > 0 &&
                                        methodesOfContact?.map((methodOfContact, index) => {
                                            return (
                                                <TableRow key={methodOfContact.id}>
                                                    <TableDataCell>
                                                        <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(methodOfContact.id)}></Button>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <Checkbox name="isBlockClass" value={methodOfContact.id} onChange={() => onSelectionChange(methodOfContact.id)} />
                                                    </TableDataCell>
                                                    <TableDataCell>{methodOfContact.name}</TableDataCell>
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
            </Main>
        </DefaultLayout>
    );
};

export default MethodesOfContact;
